import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Typography, 
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextareaAutosize
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

// Custom event name for application settings type updates
const APP_SETTINGS_TYPE_UPDATED_EVENT = 'app_settings_type_updated';

// Input types equivalent to the Laravel INPUT_TYPES constant
const INPUT_TYPES = {
  text: 'TextBox',
  textarea: 'Textarea Normal',
  texteditor: 'Textarea Editor',
  number: 'Number',
  select: 'Select',
  radio: 'Radio',
  checkbox: 'Checkbox',
  date: 'Date',
  file: 'File',
'multiple-files': 'Multiple Files',
  heading: 'Heading',
  color:'color'
};

const AddApplicationSettings = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    field_name: '',
    slug: '',
    input_type: '',
    options: '',
    application_setting_category_id: '',
    application_setting_type_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);

  // Function to convert string to slug
  const convertToSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w ]+/g, '')
      .replace(/ +/g, '-');
  };

  // Fetch categories and types on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch categories
        const categoriesResponse = await axios.get(
          API_ENDPOINTS.APPLICATION_CATEGORIES?.BASE || `${API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'categories')}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setCategories(categoriesResponse.data.data || []);

        // Fetch types
        const typesResponse = await axios.get(
          API_ENDPOINTS.APPLICATION_TYPES.BASE,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        setTypes(typesResponse.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch categories or types. Please try again.');
      }
    };

    fetchData();
  }, [navigate]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for input_type to show/hide options
    if (name === 'input_type') {
      const shouldShowOptions = ['select', 'radio', 'checkbox'].includes(value);
      setShowOptions(shouldShowOptions);
    }

    // Generate slug automatically from field_name
    if (name === 'field_name') {
      setFormData({
        ...formData,
        [name]: value,
        slug: convertToSlug(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login');
        return;
      }

      // Validate required fields
      if (!formData.field_name || !formData.input_type || !formData.application_setting_type_id) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Validate options for select, radio, checkbox
      if (['select', 'radio', 'checkbox'].includes(formData.input_type) && !formData.options) {
        setError('Options are required for the selected input type');
        setLoading(false);
        return;
      }

      // Send request to create application setting
      const response = await axios.post(
        API_ENDPOINTS.APPLICATION_SETTINGS?.BASE || `${API_ENDPOINTS.APPLICATION_TYPES.BASE.replace('types', 'settings')}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        
        // Dispatch custom event to trigger sidebar refresh
        window.dispatchEvent(new CustomEvent(APP_SETTINGS_TYPE_UPDATED_EVENT));
        
        setTimeout(() => {
          navigate('/application-settings');
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating application setting:', error);
      setError(error.response?.data?.message || 'Failed to create application setting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {/* <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={() => navigate('/application-settings')} 
              sx={{ mr: 2 }}
            >
              Back 
            </Button> */}
            {/* <Typography variant="h5">
              Create New Application Setting
            </Typography> */}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Application setting created successfully!
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* Field Name Field */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Field Name"
                  name="field_name"
                  value={formData.field_name}
                  onChange={handleChange}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              {/* Slug Field */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Slug"
                  name="slug"
                  value={formData.slug}
                  InputProps={{
                    readOnly: true,
                  }}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              
              {/* Input Type Field */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel id="input-type-label">Select Input Type</InputLabel>
                  <Select
                    labelId="input-type-label"
                    id="input_type"
                    name="input_type"
                    value={formData.input_type}
                    onChange={handleChange}
                    label="Select Input Type"
                  >
                    <MenuItem value="">Select Input Type</MenuItem>
                    {Object.entries(INPUT_TYPES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Options Field - Only shown for select, radio, checkbox */}
              {showOptions && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Options"
                    name="options"
                    value={formData.options}
                    onChange={handleChange}
                    required
                    multiline
                    rows={4}
                    helperText="Enter options, one per line"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              )}
              
              {/* Category Field */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel id="category-label">Select Category</InputLabel>
                  <Select
                    labelId="category-label"
                    id="application_setting_category_id"
                    name="application_setting_category_id"
                    value={formData.application_setting_category_id}
                    onChange={handleChange}
                    label="Select Category"
                  >
                    <MenuItem value="">Select Category</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Type Field */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel id="type-label">Select Type</InputLabel>
                  <Select
                    labelId="type-label"
                    id="application_setting_type_id"
                    name="application_setting_type_id"
                    value={formData.application_setting_type_id}
                    onChange={handleChange}
                    label="Select Type"
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    {types.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Create Application Setting'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate('/application-settings')}
                    sx={{ ml: 2 }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default AddApplicationSettings;
